import {
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  SingleSelectDropdownProps} from '@tableau/widgets';
import React from 'react';

import { openReactActionDialog,ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISubscriptionInfo } from '../models/ISubscriptionInfo';
import { Subscriptions } from '../models/Subscriptions';
import {
  allDropdownsContainerStyle,
  leftDropdownStyle,
  optionsDropdownsContainerStyle,
  rightDropdownStyle} from '../styles/SubscriptionChangeFormatDialogStyles';

export const formatDropdownTestId = 'change-format-dropdown';
export const pageSizeOptionDropdownTestId = 'change-pdf-paper-size-dropdown';
export const pageOrientationDropdownTestId = 'change-pdf-orientation-dropdown';
export const changeFormatDropdownContainerTestId = 'change-format-dropdowns-container';

const formatDropdownLabel = Messages.subscriptionFormat;
const pageSizeOptionDropdownLabel = Messages.pdfOptionsDropdownLabelPageSize;
const pageOrientationDropdownLabel = Messages.pdfOptionsDropdownLabelOrientation;

export enum SubscriptionFormat {
  Image,
  PDF,
  ImageAndPDF,
}

const FormatMap = new Map<SubscriptionFormat, () => string>()
.set(SubscriptionFormat.Image, Messages.changeSubscriptionsFormatOptionImage)
.set(SubscriptionFormat.PDF, Messages.changeSubscriptionsFormatOptionPDF)
.set(SubscriptionFormat.ImageAndPDF, Messages.changeSubscriptionsFormatOptionImagePDF);

const PageOrientationMap = new Map<PageOrientationServerString, () => string>()
.set('PORTRAIT', Messages.pdfOptionsOrientationPortrait)
.set('LANDSCAPE', Messages.pdfOptionsOrientationLandscape);

export type PageOrientationServerString =
  'PORTRAIT' |
  'LANDSCAPE';

const PdfPageSizeMap = new Map<PdfPageSizeServerString, () => string>()
.set('LETTER', Messages.pdfOptionsPageSizeLetter)
.set('LEGAL', Messages.pdfOptionsPageSizeLegal)
.set('NOTE', Messages.pdfOptionsPageSizeNote)
.set('FOLIO', Messages.pdfOptionsPageSizeFolio)
.set('TABLOID', Messages.pdfOptionsPageSizeTabloid)
.set('LEDGER', Messages.pdfOptionsPageSizeLedger)
.set('STATEMENT', Messages.pdfOptionsPageSizeStatement)
.set('EXECUTIVE', Messages.pdfOptionsPageSizeExecutive)
.set('A3', Messages.pdfOptionsPageSizeA3)
.set('A4', Messages.pdfOptionsPageSizeA4)
.set('A5', Messages.pdfOptionsPageSizeA5)
.set('B4', Messages.pdfOptionsPageSizeB4)
.set('B5', Messages.pdfOptionsPageSizeB5)
.set('QUARTO', Messages.pdfOptionsPageSizeQuarto)
.set('UNSPECIFIED', Messages.pdfOptionsPageSizeUnspecified);

export type PdfPageSizeServerString =
  'A3'|
  'A4'|
  'A5'|
  'B4'|
  'B5'|
  'EXECUTIVE'|
  'FOLIO'|
  'LEDGER'|
  'LEGAL'|
  'LETTER'|
  'NOTE'|
  'QUARTO'|
  'STATEMENT'|
  'TABLOID'|
  'UNSPECIFIED'
  ;

/**
 * @return the string in english for use with server APIs
 * @param localizedString localized orientation or page size saved in component state
 * @param stringGeneratorOptions either PdfPageSizeOptions or PageOrientationOptions
 * @param serverSafeStrings list of server safe strings
 */
function localStringToServerString(
  localizedString: string,
  stringMap: Map<PdfPageSizeServerString | PageOrientationServerString, () => string>): string {
  let serverString = Array.from(stringMap.keys()).find(serverString => stringMap.get(serverString)() === localizedString);
  if (serverString) {
    return serverString;
  }
  throw new Error('String ' + localizedString + ' not found in ' + stringMap);
}

function getDropdownListFromMap(stringMap: Map<PdfPageSizeServerString | PageOrientationServerString | SubscriptionFormat, () => string>): JSX.Element {
  const results: ListItem[] = Array.from(stringMap.values()).map(stringGenerator => {
    return {value: stringGenerator()};
  });
  return <List items={results} />;
}

function convertStringToSubscriptionFormat(format: string): SubscriptionFormat {
  return Array.from(FormatMap.values()).findIndex((candidate) => candidate()===format);
}

export function convertAttachBoolsToSubscriptionFormat(attachImage: boolean, attachPdf: boolean): SubscriptionFormat {
  if (attachImage && attachPdf) {
    return SubscriptionFormat.ImageAndPDF;
  }
  else if (attachImage) {
    return SubscriptionFormat.Image;
  }
  else if (attachPdf) {
    return SubscriptionFormat.PDF;
  }
  else {
    throw new Error('Subscription must have format of Image, PDF, or both');
  }
}

/**
 * @returns array where first value is attachImage boolean, second value is attachPdf boolean
*/

function convertSubscriptionFormatToAttachBools(subscriptionFormat: SubscriptionFormat): [boolean, boolean] {
  switch (subscriptionFormat) {
    case SubscriptionFormat.Image:
      return [true, false];
    case SubscriptionFormat.PDF:
      return [false, true];
    case SubscriptionFormat.ImageAndPDF:
      return [true, true];
    default:
      throw new Error('Invalid SubscriptionFormat passed to enum-to-boolean conversion function');
  }

}

export type SubscriptionChangeFormatDialogProps = {
  updateState: Function;
  subscriptions: ISubscriptionInfo[];
  initialFormat: SubscriptionFormat;
  initialPageOrientation: PageOrientationServerString;
  initialPageSizeOption: PdfPageSizeServerString;
};

export type SubscriptionChangeFormatDialogState = {
  format: SubscriptionFormat;
  pageOrientation: PageOrientationServerString;
  pageSizeOption: PdfPageSizeServerString;
};

export type ChangeSubscriptionFormatConfirmActionDialogBodyProps = ReactActionDialogBodyProps<
  SubscriptionChangeFormatDialogProps,
  SubscriptionChangeFormatDialogState
>;

type DropdownProps = {
  label: string;
  testId: string;
  value: string;
  stringMap: Map<PdfPageSizeServerString | PageOrientationServerString | SubscriptionFormat, () => string>;
  onChange: Function;
}

function MakeDropdown(props: DropdownProps): JSX.Element {
  const dropdownProps: SingleSelectDropdownProps = {
    label: props.label,
    testId: props.testId,
    type: DropdownButtonType.Outline,
    onChange: (value: string) => {
      props.onChange(value);
    },
    value: props.value,
    list: getDropdownListFromMap(props.stringMap)
  };

  return new SingleSelectDropdown(dropdownProps).render();
}

function isValuePdf(value: string){
  return value === FormatMap.get(SubscriptionFormat.ImageAndPDF)() || value === FormatMap.get(SubscriptionFormat.PDF)();
}

function PdfOptionsDropdowns(props: FormatDialogDropdownProps): JSX.Element {
  return <div style={optionsDropdownsContainerStyle}>
    <div style={leftDropdownStyle}>
      <MakeDropdown
        label={pageSizeOptionDropdownLabel()}
        testId={pageSizeOptionDropdownTestId}
        value={props.pageSizeOption}
        stringMap={PdfPageSizeMap}
        onChange={(value: string)=>{
          props.updateState({pageSizeOption: localStringToServerString(value, PdfPageSizeMap)});
        }}
      />
    </div>
    <div style={rightDropdownStyle}>
      <MakeDropdown
        label={pageOrientationDropdownLabel()}
        testId={pageOrientationDropdownTestId}
        value={props.pageOrientation}
        stringMap={PageOrientationMap}
        onChange={(value: string)=>{
          props.updateState({pageOrientation: localStringToServerString(value, PageOrientationMap)});
        }}
      />
    </div>
  </div>;
}

type FormatDialogDropdownProps = {
  format: string;
  pageSizeOption: string;
  pageOrientation: string;
  updateState: Function;
}

function FormatDialogDropdowns(props: FormatDialogDropdownProps): JSX.Element {
  return <div style={allDropdownsContainerStyle}
    data-tb-test-id={changeFormatDropdownContainerTestId}>
  <MakeDropdown // Select Format Dropdown
    label={formatDropdownLabel()}
    testId={formatDropdownTestId}
    value={props.format}
    stringMap={FormatMap}
    onChange={(value: string)=>{
      props.updateState({format: convertStringToSubscriptionFormat(value)});
    }}
  />
  {isValuePdf(props.format) && <PdfOptionsDropdowns
    format={props.format}
    pageSizeOption={props.pageSizeOption}
    pageOrientation={props.pageOrientation}
    updateState={props.updateState}
  />}
  </div>;
}

function setNullPdfOptionsToDefault(state: SubscriptionChangeFormatDialogState): SubscriptionChangeFormatDialogState {
  const pageSizeOption = state.pageSizeOption ? state.pageSizeOption : 'LETTER';
  const pageOrientation = state.pageOrientation ? state.pageOrientation: 'PORTRAIT';
  return {...state, ...{pageSizeOption, pageOrientation} };
}

export function shouldButtonBeDisabled(props: SubscriptionChangeFormatDialogProps, state: SubscriptionChangeFormatDialogState): boolean {
  return props.initialFormat === state.format &&
    props.initialPageOrientation === state.pageOrientation &&
    props.initialPageSizeOption === state.pageSizeOption;
}

export function SubscriptionChangeFormatDialogBody(
  props: ChangeSubscriptionFormatConfirmActionDialogBodyProps
): JSX.Element {
  // check for null values in state and set them to defaults (letter, portrait) if found
  const state = setNullPdfOptionsToDefault(props.dialogState);
  props.updateState(state);
  return <>
    <FormatDialogDropdowns
        format={FormatMap.get(state.format)()}
        pageSizeOption={PdfPageSizeMap.get(state.pageSizeOption)()}
        pageOrientation={PageOrientationMap.get(state.pageOrientation)()}
        updateState={(newStateComponent: SubscriptionChangeFormatDialogState) => {
          const mergedState={...state, ...newStateComponent};
          props.updateDialogState(mergedState);
        }}
    />
  </>;
}

export function setSubscriptionFormat(props: SubscriptionChangeFormatDialogProps, state: SubscriptionChangeFormatDialogState): Promise<IResult> {
  const [attachImage, attachPdf] = convertSubscriptionFormatToAttachBools(state.format);
  const pageSizeOption = state.pageSizeOption;
  const pageOrientation = state.pageOrientation;

  return Subscriptions.changeFormat(
    props.subscriptions.map((sub) => {return sub.id;}),
    attachImage,
    attachPdf,
    pageSizeOption,
    pageOrientation).then((
      result: IResult) => {
        if (result.errors && result.errors.length) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.changeSubscriptionsFormatFailure(),
            testId: 'change-subscription-format-failure'
          });
        } else {
          ToasterService.instance.pop({
            type: ToastType.Info,
            text: Messages.changeSubscriptionsFormatSuccess(),
            testId: 'change-subscription-format-success'
          });
        }
        return result;
      }
    );
}

export function openChangeSubscriptionsFormatDialog(subscriptions: ISubscriptionInfo[]) {
  const subscription = subscriptions[0];
  const modalInstance = openReactActionDialog<SubscriptionChangeFormatDialogProps, SubscriptionChangeFormatDialogState, IResult>({
    title: Messages.confirmChangeSubscriptionsFormat(),
    bodyComponent: SubscriptionChangeFormatDialogBody,
    initialBodyProps: {
      updateState: () => {},
      subscriptions,
      initialFormat: convertAttachBoolsToSubscriptionFormat(subscription.attachImage, subscription.attachPdf),
      initialPageOrientation: subscription.pageOrientation,
      initialPageSizeOption: subscription.pageSizeOption,
    },
    initialDialogState: {
      format: convertAttachBoolsToSubscriptionFormat(subscription.attachImage, subscription.attachPdf),
      pageOrientation: subscription.pageOrientation,
      pageSizeOption: subscription.pageSizeOption,
    },
    actionName: Messages.confirmChangeSubscriptionsFormat(),
    action: setSubscriptionFormat,
    actionDisabled: shouldButtonBeDisabled,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
  });

  return modalInstance.result;
}
